import { useState, useEffect, useCallback, useRef } from 'react';
import { collection, query, where, orderBy, addDoc, updateDoc, deleteDoc, doc, arrayUnion, arrayRemove, getDocs, getDoc, limit } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import { Classroom, ClassroomMessage, PrivateMessage } from '../types';
import { useAuth } from '../contexts/AuthContext';

export const useClassrooms = () => {
  const { currentUser } = useAuth();
  const [classrooms, setClassrooms] = useState<Classroom[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Simple cache to avoid duplicate requests
  const cache = useRef(new Map<string, Classroom>());
  const initialized = useRef(false);
  const hookInstanceId = useRef(Math.random().toString(36).substr(2, 9));

  // Lightweight cache (localStorage) to reduce read costs
  const getUserCacheKey = (uid: string) => `classrooms_cache_v2_${uid}`;
  const CACHE_TTL_MS = 60 * 60 * 1000; // 60 minutes
  const QUERY_LIMIT = 24;
  const lastNetworkFetchRef = useRef<number>(0);

  // Seed classrooms from cache only (no network)
  const seedFromCache = useCallback((): boolean => {
    if (!currentUser) return false;
    try {
      const cachedRaw = localStorage.getItem(getUserCacheKey(currentUser.uid));
      if (cachedRaw) {
        const cached = JSON.parse(cachedRaw) as { data: Classroom[]; updatedAt: number };
        if (Array.isArray(cached?.data)) {
          setClassrooms(cached.data);
          return true;
        }
      }
    } catch {}
    return false;
  }, [currentUser]);

  // Debug logging only in development and only once per hook instance
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log(`[useClassrooms:${hookInstanceId.current}] Hook initialized`);
  }

  // Seed from cache on first load (avoid extra reads and flicker)
  useEffect(() => {
    if (!currentUser || initialized.current) return;
    initialized.current = true;
    seedFromCache();
  }, [currentUser?.uid, seedFromCache]);

  // Reset when user changes
  useEffect(() => {
    if (!currentUser) {
      setClassrooms([]);
      setLoading(false);
      setError(null);
      initialized.current = false;
      cache.current.clear();
    }
  }, [currentUser?.uid]);

  // Create classroom
  const createClassroom = useCallback(async (classroomData: Omit<Classroom, 'id' | 'createdAt' | 'updatedAt' | 'memberCount' | 'messages' | 'privateMessages' | 'admins'>) => {
    if (!currentUser) throw new Error('User not authenticated');
    
    try {
      const newClassroom = {
        ...classroomData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        memberCount: 1,
        messages: [],
        privateMessages: [],
        admins: [classroomData.createdBy]
      };
      
      const docRef = await addDoc(collection(db, 'classrooms'), newClassroom);
      const createdClassroom = { id: docRef.id, ...newClassroom };
      
      // Update local state
      setClassrooms(prev => [createdClassroom, ...prev]);
      cache.current.set(docRef.id, createdClassroom);
      
      return docRef.id;
    } catch (err) {
      throw new Error('Failed to create classroom');
    }
  }, [currentUser]);

  // Join classroom
  const joinClassroom = useCallback(async (classroomId: string) => {
    if (!currentUser) return;
    
    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      await updateDoc(classroomRef, {
        members: arrayUnion(currentUser.uid),
        memberCount: (classrooms.find(c => c.id === classroomId)?.memberCount || 0) + 1
      });
    } catch (err) {
      throw new Error('Failed to join classroom');
    }
  }, [currentUser, classrooms]);

  // Leave classroom
  const leaveClassroom = useCallback(async (classroomId: string) => {
    if (!currentUser) return;
    
    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      await updateDoc(classroomRef, {
        members: arrayRemove(currentUser.uid),
        memberCount: Math.max(0, (classrooms.find(c => c.id === classroomId)?.memberCount || 1) - 1)
      });
    } catch (err) {
      throw new Error('Failed to leave classroom');
    }
  }, [currentUser, classrooms]);

  // Send message
  const sendMessage = useCallback(async (classroomId: string, message: {
    senderId: string;
    senderName: string;
    text: string;
    attachments?: File[];
  }) => {
    if (!currentUser) return;

    try {
      console.log('[useClassrooms] Sending message to classroom:', classroomId, message);
      const classroomRef = doc(db, 'classrooms', classroomId);
      
      // Handle file uploads if present
      let attachmentUrls: ClassroomMessage['attachments'] = [];
      if (message.attachments && message.attachments.length > 0) {
        console.log('[useClassrooms] Uploading', message.attachments.length, 'files');
        
        const uploadPromises = message.attachments.map(async (file) => {
          const fileName = `${Date.now()}_${file.name}`;
          const storageRef = ref(storage, `classroom-attachments/${classroomId}/${fileName}`);
          
          try {
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);
            
            return {
              name: file.name,
              url: downloadURL,
              type: file.type,
              size: file.size
            };
          } catch (error) {
            console.error('[useClassrooms] Failed to upload file:', file.name, error);
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        });
        
        attachmentUrls = await Promise.all(uploadPromises);
        console.log('[useClassrooms] Files uploaded successfully:', attachmentUrls.length);
      }
      
      // First, get the current classroom document to access existing messages
      const classroomDoc = await getDoc(classroomRef);
      if (!classroomDoc.exists()) {
        throw new Error('Classroom not found');
      }
      
      const currentData = classroomDoc.data();
      const currentMessages = currentData.messages || [];
      
      const newMessage: ClassroomMessage = {
        senderId: message.senderId,
        senderName: message.senderName,
        text: message.text,
        timestamp: new Date().toISOString(),
        ...(attachmentUrls.length > 0 && { attachments: attachmentUrls })
      };
      
      console.log('[useClassrooms] New message object:', newMessage);
      console.log('[useClassrooms] Current messages count:', currentMessages.length);
      
      // Create updated messages array
      const updatedMessages = [...currentMessages, newMessage];
      
      // Update the document with the new messages array
      await updateDoc(classroomRef, {
        messages: updatedMessages,
        updatedAt: new Date().toISOString()
      });

      console.log('[useClassrooms] Message sent successfully to Firestore. New message count:', updatedMessages.length);

      // Update local state
      setClassrooms(prev => prev.map(c => 
          c.id === classroomId 
          ? { ...c, messages: updatedMessages, updatedAt: new Date().toISOString() }
          : c
      ));
    } catch (err) {
      console.error('[useClassrooms] Failed to send message:', err);
      throw new Error('Failed to send message');
    }
  }, [currentUser]);

  // Send private message
  const sendPrivateMessage = useCallback(async (classroomId: string, message: Omit<PrivateMessage, 'timestamp'>) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const newMessage: PrivateMessage = {
        ...message,
        timestamp: new Date().toISOString()
      };
      
      await updateDoc(classroomRef, {
        privateMessages: arrayUnion(newMessage),
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      throw new Error('Failed to send private message');
    }
  }, [currentUser]);

  // Check if user is admin
  const isAdmin = useCallback((classroom: Classroom, userId: string) => {
    return classroom.admins?.includes(userId) || classroom.createdBy === userId;
  }, []);

  // Add member
  const addMember = useCallback(async (classroomId: string, memberEmail: string) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const currentClassroom = classrooms.find(c => c.id === classroomId);
      
      if (!currentClassroom || !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('Only admins can add members');
      }

      const newMemberId = `member_${Date.now()}`;
      await updateDoc(classroomRef, {
        members: arrayUnion(newMemberId),
        memberCount: currentClassroom.memberCount + 1,
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      throw new Error('Failed to add member');
    }
  }, [currentUser, classrooms, isAdmin]);

  // Remove member
  const removeMember = useCallback(async (classroomId: string, memberId: string) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const currentClassroom = classrooms.find(c => c.id === classroomId);
      
      if (!currentClassroom || !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('Only admins can remove members');
      }

      await updateDoc(classroomRef, {
        members: arrayRemove(memberId),
        memberCount: Math.max(0, currentClassroom.memberCount - 1),
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      throw new Error('Failed to remove member');
    }
  }, [currentUser, classrooms, isAdmin]);

  // Add admin
  const addAdmin = useCallback(async (classroomId: string, memberId: string) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const currentClassroom = classrooms.find(c => c.id === classroomId);
      
      if (!currentClassroom || !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('Only admins can add other admins');
      }
      
      await updateDoc(classroomRef, {
        admins: arrayUnion(memberId),
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      throw new Error('Failed to add admin');
    }
  }, [currentUser, classrooms, isAdmin]);

  // Remove admin
  const removeAdmin = useCallback(async (classroomId: string, adminId: string) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const currentClassroom = classrooms.find(c => c.id === classroomId);
      
      if (!currentClassroom || !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('Only admins can remove other admins');
      }

      const currentAdmins = currentClassroom.admins || [currentClassroom.createdBy];
      if (currentAdmins.length === 1 && currentAdmins[0] === adminId) {
        throw new Error('Cannot remove the last admin');
      }

      await updateDoc(classroomRef, {
        admins: arrayRemove(adminId),
        updatedAt: new Date().toISOString()
      });
    } catch (err) {
      throw new Error('Failed to remove admin');
    }
  }, [currentUser, classrooms, isAdmin]);

  // Update classroom
  const updateClassroom = useCallback(async (classroomId: string, updates: Partial<Omit<Classroom, 'id' | 'createdAt' | 'createdBy' | 'createdByUid' | 'members' | 'memberCount' | 'messages' | 'privateMessages' | 'admins'>>) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const currentClassroom = classrooms.find(c => c.id === classroomId);
      
      if (!currentClassroom || !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('Only admins can update classroom');
      }

      await updateDoc(classroomRef, {
        ...updates,
        updatedAt: new Date().toISOString()
      });

      // Update local state
      setClassrooms(prev => prev.map(c => 
        c.id === classroomId 
          ? { ...c, ...updates, updatedAt: new Date().toISOString() }
          : c
      ));
    } catch (err) {
      throw new Error('Failed to update classroom');
    }
  }, [currentUser, classrooms, isAdmin]);

  // Delete classroom
  const deleteClassroom = useCallback(async (classroomId: string) => {
    if (!currentUser) return;

    try {
      const classroomRef = doc(db, 'classrooms', classroomId);
      const currentClassroom = classrooms.find(c => c.id === classroomId);
      
      if (!currentClassroom || !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('Only admins can delete classroom');
      }

      // Delete image if exists
      if (currentClassroom.profileImageUrl?.startsWith('classroom-images/')) {
        try {
          const imageRef = ref(storage, currentClassroom.profileImageUrl);
          await deleteObject(imageRef);
        } catch (err) {
          // Ignore storage deletion errors
        }
      }

      await deleteDoc(classroomRef);
      
      // Remove from local state
      setClassrooms(prev => prev.filter(c => c.id !== classroomId));
      cache.current.delete(classroomId);
    } catch (err) {
      throw new Error('Failed to delete classroom');
    }
  }, [currentUser, classrooms, isAdmin]);

  // Delete message
  const deleteMessage = useCallback(async (classroomId: string, messageIndex: number, classroomData?: Classroom) => {
    if (!currentUser) return;

    try {
      console.log('[useClassrooms] Deleting message from classroom:', classroomId, 'at index:', messageIndex);
      const classroomRef = doc(db, 'classrooms', classroomId);
      
      // Use provided classroom data or find in local state
      let currentClassroom = classroomData || classrooms.find(c => c.id === classroomId);
      
      console.log('[useClassrooms] Found classroom in local state:', currentClassroom ? 'yes' : 'no');
      
      if (!currentClassroom) {
        // If not found locally, fetch from Firestore
        console.log('[useClassrooms] Classroom not found locally, fetching from Firestore...');
        const classroomDoc = await getDoc(classroomRef);
        if (!classroomDoc.exists()) {
          throw new Error('Classroom not found');
        }
        currentClassroom = { id: classroomDoc.id, ...classroomDoc.data() } as Classroom;
      }

      if (!currentClassroom.messages || !Array.isArray(currentClassroom.messages)) {
        throw new Error('No messages found in classroom');
      }

      if (messageIndex < 0 || messageIndex >= currentClassroom.messages.length) {
        throw new Error(`Invalid message index: ${messageIndex}. Messages count: ${currentClassroom.messages.length}`);
      }

      const message = currentClassroom.messages[messageIndex];
      if (!message) {
        throw new Error('Message not found');
      }

      console.log('[useClassrooms] Message to delete:', message);
      console.log('[useClassrooms] Current messages count:', currentClassroom.messages.length);
      console.log('[useClassrooms] User permissions - senderId:', message.senderId, 'currentUser:', currentUser.uid, 'isAdmin:', isAdmin(currentClassroom, currentUser.uid));

      if (message.senderId !== currentUser.uid && !isAdmin(currentClassroom, currentUser.uid)) {
        throw new Error('You can only delete your own messages');
      }

      const updatedMessages = [...currentClassroom.messages];
      updatedMessages.splice(messageIndex, 1);

      console.log('[useClassrooms] Updated messages count:', updatedMessages.length);
      console.log('[useClassrooms] About to update Firestore document...');

      await updateDoc(classroomRef, {
        messages: updatedMessages,
        updatedAt: new Date().toISOString()
      });

      console.log('[useClassrooms] Message deleted successfully from Firestore');
      
      // Update local state
      setClassrooms(prev => prev.map(c => 
          c.id === classroomId 
            ? { ...c, messages: updatedMessages, updatedAt: new Date().toISOString() }
            : c
      ));
      
      console.log('[useClassrooms] Local state updated');
    } catch (err) {
      console.error('[useClassrooms] Failed to delete message:', err);
      console.error('[useClassrooms] Error details:', {
        message: err instanceof Error ? err.message : 'Unknown error',
        stack: err instanceof Error ? err.stack : 'No stack trace',
        error: err
      });
      throw new Error(`Failed to delete message: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [currentUser, classrooms, isAdmin]);

  // Upload image
  const uploadClassroomImage = useCallback(async (file: File, classroomId?: string): Promise<string> => {
    if (!currentUser) throw new Error('User not authenticated');

    try {
      if (!file.type.startsWith('image/')) {
        throw new Error('File must be an image');
      }

      if (file.size > 5 * 1024 * 1024) {
        throw new Error('Image size must be less than 5MB');
      }

      const timestamp = Date.now();
      const fileName = `classroom-images/${classroomId || 'new'}/${timestamp}_${file.name}`;
      const storageRef = ref(storage, fileName);

      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return downloadURL;
    } catch (err) {
      throw new Error('Failed to upload image');
    }
  }, [currentUser]);

  // Delete image
  const deleteClassroomImage = useCallback(async (imageUrl: string): Promise<void> => {
    try {
      const imageRef = ref(storage, imageUrl);
      await deleteObject(imageRef);
    } catch (err) {
      throw new Error('Failed to delete image');
    }
  }, []);

  // Get public classrooms (for non-authenticated users)
  const getPublicClassrooms = useCallback(async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(
        query(
          collection(db, 'classrooms'),
          where('status', '==', 'active'),
          orderBy('updatedAt', 'desc'),
          limit(QUERY_LIMIT)
        )
      );
      
      const classroomData: Classroom[] = [];
      snapshot.forEach(doc => {
        const data = doc.data();
        if (!data.admins) data.admins = [data.createdByUid];
        classroomData.push({ id: doc.id, ...data } as Classroom);
      });
      
      setClassrooms(classroomData);
      return classroomData;
    } catch (err) {
      setError('Failed to fetch public classrooms');
      throw new Error('Failed to fetch public classrooms');
    } finally {
      setLoading(false);
    }
  }, []);

  // Get all user classrooms (created + member-of) with caching and minimal reads
  const getAllUserClassrooms = useCallback(async () => {
    if (!currentUser) return [];
    
    try {
      // Check cache first
      try {
        const cachedRaw = localStorage.getItem(getUserCacheKey(currentUser.uid));
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { data: Classroom[]; updatedAt: number };
          if (cached?.data && Array.isArray(cached.data) && cached.updatedAt && (Date.now() - cached.updatedAt) < CACHE_TTL_MS) {
            return cached.data as Classroom[];
          }
        }
      } catch {}

      // Throttle network fetches if we fetched very recently
      const now = Date.now();
      if (lastNetworkFetchRef.current && now - lastNetworkFetchRef.current < 2 * 60 * 1000) {
        // Return current state or stale cache if available to avoid extra reads
        try {
          const cachedRaw = localStorage.getItem(getUserCacheKey(currentUser.uid));
          if (cachedRaw) {
            const cached = JSON.parse(cachedRaw) as { data: Classroom[]; updatedAt: number };
            if (Array.isArray(cached?.data)) {
              return cached.data;
            }
          }
        } catch {}
        return classrooms;
      }

      // Minimal reads: two queries (created and membership)
      const [createdSnap, memberSnap] = await Promise.all([
        getDocs(query(collection(db, 'classrooms'), where('createdByUid', '==', currentUser.uid), limit(QUERY_LIMIT))),
        getDocs(query(collection(db, 'classrooms'), where('members', 'array-contains', currentUser.uid), limit(QUERY_LIMIT)))
      ]);

      const map = new Map<string, Classroom>();
      createdSnap.forEach(d => {
        const data = { id: d.id, ...d.data() } as Classroom;
        map.set(d.id, data);
      });
      memberSnap.forEach(d => {
        const data = { id: d.id, ...d.data() } as Classroom;
        map.set(d.id, data);
      });

      // Sort by updatedAt desc client-side
      const toMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value === 'string' || typeof value === 'number') {
            const ms = new Date(value).getTime();
            return isNaN(ms) ? 0 : ms;
          }
          if (typeof value?.toDate === 'function') {
            return value.toDate().getTime();
          }
          if (typeof value?.seconds === 'number') {
            return value.seconds * 1000 + (value.nanoseconds || 0) / 1e6;
          }
          return 0;
        } catch {
          return 0;
        }
      };

      const result = Array.from(map.values()).sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));

      // Write to cache
      try {
        localStorage.setItem(getUserCacheKey(currentUser.uid), JSON.stringify({ data: result, updatedAt: Date.now() }));
      } catch {}

      lastNetworkFetchRef.current = Date.now();

      return result;
    } catch (err) {
      throw new Error('Failed to fetch user classrooms');
    }
  }, [currentUser]);

  // Get all classrooms (both active and inactive, no user restrictions)
  const getAllClassrooms = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Query all classrooms without any filters
      const snapshot = await getDocs(
        query(
          collection(db, 'classrooms'),
          orderBy('updatedAt', 'desc'),
          limit(QUERY_LIMIT * 2) // Allow more results for "all" query
        )
      );
      
      const allClassrooms: Classroom[] = [];
      snapshot.forEach(doc => {
        const data = { id: doc.id, ...doc.data() } as Classroom;
        // Ensure admins array is properly set
        if (!data.admins) data.admins = [data.createdBy];
        allClassrooms.push(data);
      });
      
      // Update local state
      setClassrooms(allClassrooms);
      
      // Cache result
      try {
        localStorage.setItem('all_classrooms_cache', JSON.stringify({ 
          data: allClassrooms, 
          updatedAt: Date.now() 
        }));
      } catch {}
      
      return allClassrooms;
    } catch (err) {
      setError('Failed to fetch all classrooms');
      throw new Error('Failed to fetch all classrooms');
    } finally {
      setLoading(false);
    }
  }, []);

  // Get author classrooms (classrooms created by the current user)
  const getAuthorClassrooms = useCallback(async () => {
    if (!currentUser) {
      setClassrooms([]);
      return [];
    }
    
    try {
      setLoading(true);
      setError(null);

      // Query classrooms where createdBy OR createdByUid matches current user
      // This handles both possible field names
      const [createdBySnap, createdByUidSnap] = await Promise.all([
        getDocs(
          query(
            collection(db, 'classrooms'),
            where('createdBy', '==', currentUser.uid),
            orderBy('updatedAt', 'desc'),
            limit(QUERY_LIMIT)
          )
        ),
        getDocs(
          query(
            collection(db, 'classrooms'),
            where('createdByUid', '==', currentUser.uid),
            orderBy('updatedAt', 'desc'),
            limit(QUERY_LIMIT)
          )
        )
      ]);


      const authorClassrooms: Classroom[] = [];
      const seenIds = new Set<string>();

      // Process createdBy results
      createdBySnap.forEach(doc => {
        if (!seenIds.has(doc.id)) {
          const docData = doc.data();
          const data = { id: doc.id, ...docData } as Classroom;
          // Ensure admins array is properly set
          if (!data.admins) {
            data.admins = [data.createdBy || (docData as any).createdByUid || ''];
          }
          authorClassrooms.push(data);
          seenIds.add(doc.id);
        }
      });

      // Process createdByUid results
      createdByUidSnap.forEach(doc => {
        if (!seenIds.has(doc.id)) {
          const docData = doc.data();
          const data = { id: doc.id, ...docData } as Classroom;
          // Ensure admins array is properly set
          if (!data.admins) {
            data.admins = [data.createdBy || (docData as any).createdByUid || ''];
          }
          authorClassrooms.push(data);
          seenIds.add(doc.id);
        }
      });

      // Sort by updatedAt desc
      const toMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value === 'string' || typeof value === 'number') {
            const ms = new Date(value).getTime();
            return isNaN(ms) ? 0 : ms;
          }
          if (typeof value?.toDate === 'function') return value.toDate().getTime();
          if (typeof value?.seconds === 'number') return value.seconds * 1000 + (value.nanoseconds || 0) / 1e6;
          return 0;
        } catch { return 0; }
      };

      const sortedClassrooms = authorClassrooms.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));

      // If no classrooms found with the specific queries, try a fallback approach
      if (sortedClassrooms.length === 0) {
        try {
          // Get all classrooms and filter client-side
          const allClassroomsSnap = await getDocs(
            query(
              collection(db, 'classrooms'),
              orderBy('updatedAt', 'desc'),
              limit(50)
            )
          );
          
          const allClassrooms: Classroom[] = [];
          allClassroomsSnap.forEach(doc => {
            const docData = doc.data();
            const isAuthorClassroom = 
              docData.createdBy === currentUser.uid || 
              docData.createdByUid === currentUser.uid ||
              docData.createdBy1 === currentUser.uid ||
              docData.createdBy === currentUser.email ||
              docData.createdByUid === currentUser.email ||
              docData.createdBy1 === currentUser.email;
            
            if (isAuthorClassroom) {
              const data = { id: doc.id, ...docData } as Classroom;
              if (!data.admins) {
                data.admins = [data.createdBy || (docData as any).createdByUid || (docData as any).createdBy1 || ''];
              }
              allClassrooms.push(data);
            }
          });
          
          
          if (allClassrooms.length > 0) {
            const sortedFallback = allClassrooms.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));
            setClassrooms(sortedFallback);
            return sortedFallback;
          }
        } catch (fallbackErr) {
          console.error('[getAuthorClassrooms] Fallback query failed:', fallbackErr);
        }
      }

      // Update local state with only author classrooms (even if empty)
      setClassrooms(sortedClassrooms);
      
      // Cache result
      try {
        localStorage.setItem(getUserCacheKey(currentUser.uid), JSON.stringify({ data: sortedClassrooms, updatedAt: Date.now() }));
      } catch {}

      return sortedClassrooms;
    } catch (err) {
      console.error('Error fetching author classrooms:', err);
      // Set empty array instead of throwing error
      setClassrooms([]);
      setError(null); // Clear error for empty results
      return [];
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  // Fetch classrooms tied to courses the user bought (enrolled) or authored
  const fetchEnrolledCourseClassrooms = useCallback(async () => {
    if (!currentUser) return;

    setLoading(true);
    setError(null);

    try {
      // Seed from cache immediately for fast paint (SWR style)
      try {
        const cachedRaw = localStorage.getItem(getUserCacheKey(currentUser.uid));
        if (cachedRaw) {
          const cached = JSON.parse(cachedRaw) as { data: Classroom[]; updatedAt: number };
          if (Array.isArray(cached?.data)) {
            setClassrooms(cached.data);
          }
        }
      } catch {}

      // 1) Get enrolled course IDs from users/{uid}/Courses
      const enrolledCoursesSnap = await getDocs(collection(db, 'users', currentUser.uid, 'Courses'));
      const enrolledCourseIds = new Set<string>();
      enrolledCoursesSnap.forEach((d) => {
        const data: any = d.data();
        if (typeof data?.course_id === 'string' && data.course_id) {
          enrolledCourseIds.add(data.course_id);
        }
      });

      // 2) Get authored course IDs (courses where userId == currentUser.uid)
      const authoredCoursesSnap = await getDocs(
        query(collection(db, 'courses'), where('userId', '==', currentUser.uid), limit(200))
      );
      const authoredCourseIds = new Set<string>();
      authoredCoursesSnap.forEach((d) => authoredCourseIds.add(d.id));

      // Combine course IDs (avoid spreading Sets for downlevel targets)
      const courseIds = Array.from(
        new Set<string>(
          Array.from(enrolledCourseIds).concat(Array.from(authoredCourseIds))
        )
      );
      if (courseIds.length === 0) {
        setClassrooms([]);
        // Update cache
        try { localStorage.setItem(getUserCacheKey(currentUser.uid), JSON.stringify({ data: [], updatedAt: Date.now() })); } catch {}
        return;
      }

      // 3) For each course, read 'courses/{courseId}/Classrooms' to collect referenced classroom IDs
      const collectIdsForCourse = async (courseId: string): Promise<string[]> => {
        try {
          const classRef = collection(db, 'courses', courseId, 'Classrooms');
          const classSnap = await getDocs(classRef);
          const ids = new Set<string>();
          classSnap.forEach((cDoc) => {
            const d: any = cDoc.data();
            const raw = d?.classroomId;
            if (Array.isArray(raw)) {
              raw.forEach((item) => {
                if (typeof item === 'string') ids.add(item);
                else if (item && typeof item === 'object') {
                  if (typeof item.id === 'string') ids.add(item.id);
                  else if (typeof item.classroomId === 'string') ids.add(item.classroomId);
                }
              });
            } else if (typeof raw === 'string') {
              ids.add(raw);
            } else if (raw && typeof raw === 'object') {
              if (typeof raw.id === 'string') ids.add(raw.id);
              else if (typeof raw.classroomId === 'string') ids.add(raw.classroomId);
            }
          });
          return Array.from(ids);
        } catch {
          return [];
        }
      };

      const classroomIdSets = await Promise.all(courseIds.map(collectIdsForCourse));
      const classroomIds = Array.from(new Set<string>(classroomIdSets.flat().filter(Boolean)));

      if (classroomIds.length === 0) {
        setClassrooms([]);
        try { localStorage.setItem(getUserCacheKey(currentUser.uid), JSON.stringify({ data: [], updatedAt: Date.now() })); } catch {}
        return;
      }

      // 4) Fetch classroom docs by ID from main 'classrooms' collection
      const fetchClassroomDoc = async (id: string): Promise<Classroom | null> => {
        try {
          const cSnap = await getDoc(doc(db, 'classrooms', id));
          if (!cSnap.exists()) return null;
          const data: any = cSnap.data();
          // Normalize minimal fields to match Classroom type expectations
          const classroom: Classroom = {
            id: cSnap.id,
            category: data.category || '',
            createdAt: data.createdAt || new Date().toISOString(),
            createdBy: data.createdBy || data.createdByUid || '',
            createdBy1: data.createdBy1 || '',
            description: data.description || '',
            maxMembers: typeof data.maxMembers === 'number' ? data.maxMembers : 1000,
            memberCount: typeof data.memberCount === 'number' ? data.memberCount : (Array.isArray(data.members) ? data.members.length : 0),
            members: Array.isArray(data.members) ? data.members : [],
            admins: Array.isArray(data.admins) ? data.admins : (data.createdByUid ? [data.createdByUid] : (data.createdBy ? [data.createdBy] : [])),
            name: data.name || data.classroomName || cSnap.id,
            profileImageUrl: data.profileImageUrl || null,
            status: data.status === 'inactive' ? 'inactive' : 'active',
            subject: data.subject || '',
            updatedAt: data.updatedAt || data.createdAt || new Date().toISOString(),
            messages: Array.isArray(data.messages) ? data.messages : [],
            privateMessages: Array.isArray(data.privateMessages) ? data.privateMessages : [],
            zoomSettings: data.zoomSettings,
            scheduledMeetings: data.scheduledMeetings,
          } as Classroom;
          return classroom;
        } catch {
          return null;
        }
      };

      const classroomDocs = await Promise.all(classroomIds.map(fetchClassroomDoc));
      const resolvedClassrooms = classroomDocs.filter(Boolean) as Classroom[];

      // Sort by updatedAt desc
      const toMillis = (value: any): number => {
        try {
          if (!value) return 0;
          if (typeof value === 'string' || typeof value === 'number') {
            const ms = new Date(value).getTime();
            return isNaN(ms) ? 0 : ms;
          }
          if (typeof value?.toDate === 'function') return value.toDate().getTime();
          if (typeof value?.seconds === 'number') return value.seconds * 1000 + (value.nanoseconds || 0) / 1e6;
          return 0;
        } catch { return 0; }
      };

      const sorted = resolvedClassrooms.sort((a, b) => toMillis(b.updatedAt) - toMillis(a.updatedAt));

      setClassrooms(sorted);

      // Cache result
      try {
        localStorage.setItem(getUserCacheKey(currentUser.uid), JSON.stringify({ data: sorted, updatedAt: Date.now() }));
      } catch {}

    } catch (err) {
      setError('Failed to fetch enrolled course classrooms');
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  return {
    classrooms,
    loading,
    error,
    createClassroom,
    joinClassroom,
    leaveClassroom,
    sendMessage,
    sendPrivateMessage,
    isAdmin,
    addMember,
    removeMember,
    addAdmin,
    removeAdmin,
    updateClassroom,
    deleteClassroom,
    deleteMessage,
    uploadClassroomImage,
    deleteClassroomImage,
    getPublicClassrooms,
    getAllUserClassrooms,
    getAllClassrooms,
    getAuthorClassrooms,
    fetchEnrolledCourseClassrooms,
  };
}; 
